import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Tooltip,
  IconButton,
  Chip,
  Typography,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { SnackbarProvider } from 'notistack';
import { CustomTable, CustomSwitch, Toaster } from '@/components';
import AlertConfirm from '@/components/Crosscutting/AlertConfirm';
import {
  updatePaymentMethodService,
  deletePaymentMethodService,
} from '@/services';
import type { PaymentMethod } from '../PaymentMethodsScreen';

const useStyles = makeStyles((theme) => ({
  actionsContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'center',
  },
  methodsChips: {
    display: 'flex',
    gap: theme.spacing(0.5),
    flexWrap: 'wrap',
  },
  chip: {
    height: 24,
    fontSize: '0.75rem',
  },
}));

interface PaymentMethodsTableProps {
  paymentMethods: PaymentMethod[];
  setPaymentMethods: React.Dispatch<React.SetStateAction<PaymentMethod[]>>;
  enteredFilter: string;
  setPopUpAction: React.Dispatch<
    React.SetStateAction<{
      open: boolean;
      title: string;
      data: PaymentMethod | null;
    }>
  >;
  refreshData: () => void;
  actionsDisabled?: boolean;
}

export const PaymentMethodsTable: React.FC<PaymentMethodsTableProps> = ({
  paymentMethods,
  setPaymentMethods,
  enteredFilter,
  setPopUpAction,
  refreshData,
  actionsDisabled = false,
}) => {
  const classes = useStyles();
  const providerRef = React.useRef<any>(null);
  const [filteredRows, setFilteredRows] = useState<PaymentMethod[]>([]);
  const [alert, setAlert] = useState({ show: false, type: 'success' as 'success' | 'error', text: '' });
  const [openAlertConfirm, setOpenAlertConfirm] = useState(false);
  const [openAlertConfirmDelete, setOpenAlertConfirmDelete] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<{
    id: string;
    checked: boolean;
  } | null>(null);

  // Filtrar tabla según búsqueda
  useEffect(() => {
    if (!paymentMethods || !Array.isArray(paymentMethods)) {
      setFilteredRows([]);
      return;
    }

    const filtered = paymentMethods.filter(
      (method) =>
        method.name.toLowerCase().includes(enteredFilter.toLowerCase()) ||
        method.description.toLowerCase().includes(enteredFilter.toLowerCase())
    );
    setFilteredRows(filtered);
  }, [enteredFilter, paymentMethods]);

  const handleChangeState = (event: React.ChangeEvent<HTMLInputElement>, methodId: string) => {
    setSelectedMethod({
      id: methodId,
      checked: event.target.checked,
    });
    setOpenAlertConfirm(true);
  };

  const handleDeleteMethod = (methodId: string) => {
    setSelectedMethod({
      id: methodId,
      checked: false,
    });
    setOpenAlertConfirmDelete(true);
  };

  const handleEditMethod = (methodId: string) => {
    const method = paymentMethods.find((m) => m.id === methodId);
    if (method) {
      setPopUpAction({
        open: true,
        title: 'Editar Método de Pago',
        data: method,
      });
    }
  };

  const confirmChangeState = async () => {
    if (!selectedMethod) return;

    try {
      const methodToUpdate = paymentMethods.find((m) => m.id === selectedMethod.id);
      if (!methodToUpdate) return;

      const res = await updatePaymentMethodService({
        ...methodToUpdate,
        active: selectedMethod.checked,
      });

      if (res?.status === 200) {
        providerRef.current?.enqueueSnackbar(
          `Método de pago ${selectedMethod.checked ? 'activado' : 'desactivado'} exitosamente`,
          {
            variant: 'success',
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'center',
            },
          }
        );
        refreshData();
      } else {
        setAlert({
          show: true,
          type: 'error',
          text: 'Ha ocurrido un error, intente nuevamente.',
        });
      }
    } catch (error) {
      console.error('Error updating payment method:', error);
      setAlert({
        show: true,
        type: 'error',
        text: 'Ha ocurrido un error, intente nuevamente.',
      });
    }

    setOpenAlertConfirm(false);
    setSelectedMethod(null);
  };

  const confirmDelete = async () => {
    if (!selectedMethod) return;

    try {
      const res = await deletePaymentMethodService(selectedMethod.id);

      if (res?.status === 200) {
        providerRef.current?.enqueueSnackbar('Método de pago eliminado exitosamente', {
          variant: 'success',
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
          },
        });
        const updatedMethods = paymentMethods.filter((m) => m.id !== selectedMethod.id);
        setPaymentMethods(updatedMethods);
      } else {
        setAlert({
          show: true,
          type: 'error',
          text: 'Ha ocurrido un error, intente nuevamente.',
        });
      }
    } catch (error) {
      console.error('Error deleting payment method:', error);
      setAlert({
        show: true,
        type: 'error',
        text: 'Ha ocurrido un error, intente nuevamente.',
      });
    }

    setOpenAlertConfirmDelete(false);
    setSelectedMethod(null);
  };

  const renderPaymentMethods = (methods: PaymentMethod['paymentMethods']) => {
    const activeMethodsLabels: string[] = [];
    if (methods.card) activeMethodsLabels.push('Tarjeta');
    if (methods.paymentButton) activeMethodsLabels.push('Botón de pago');
    if (methods.transfer) activeMethodsLabels.push('Transferencia');

    return (
      <Box className={classes.methodsChips}>
        {activeMethodsLabels.map((label) => (
          <Chip key={label} label={label} size="small" className={classes.chip} color="primary" />
        ))}
      </Box>
    );
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Nombre',
      width: 200,
    },
    {
      field: 'terminalType',
      headerName: 'Tipo',
      width: 150,
      renderCell: (params: any) => (
        <Typography variant="body2">
          {params.row.terminalType === 'ecommerce' ? 'Ecommerce' : 'Dispositivo físico'}
        </Typography>
      ),
    },
    {
      field: 'description',
      headerName: 'Descripción',
      width: 250,
    },
    {
      field: 'price',
      headerName: 'Precio',
      width: 120,
      renderCell: (params: any) => (
        <Typography variant="body2">${params.row.price.toFixed(2)}</Typography>
      ),
    },
    {
      field: 'quantity',
      headerName: 'Cantidad',
      width: 100,
      renderCell: (params: any) => (
        <Typography variant="body2">
          {params.row.terminalType === 'physical' ? params.row.quantity : '-'}
        </Typography>
      ),
    },
    {
      field: 'chargeType',
      headerName: 'Tipo de cobro',
      width: 150,
      renderCell: (params: any) => (
        <Typography variant="body2">
          {params.row.chargeType === 'rental' ? 'Alquiler' : 'Pago único'}
        </Typography>
      ),
    },
    {
      field: 'paymentMethods',
      headerName: 'Métodos de pago',
      width: 250,
      renderCell: (params: any) => renderPaymentMethods(params.row.paymentMethods),
    },
    {
      field: 'active',
      headerName: 'Estado',
      width: 100,
      renderCell: (params: any) => (
        <CustomSwitch
          check={params.row.active}
          handleChange={(e: any) => handleChangeState(e, params.row.id)}
          checked={params.row.active}
          value={params.row.id}
          disabled={actionsDisabled}
          lblOff=""
          lblOn=""
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 120,
      renderCell: (params: any) => (
        <Box className={classes.actionsContainer}>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              onClick={() => handleEditMethod(params.row.id)}
              disabled={actionsDisabled}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton
              size="small"
              onClick={() => handleDeleteMethod(params.row.id)}
              disabled={actionsDisabled}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <>
      <SnackbarProvider ref={providerRef} maxSnack={3}>
        <CustomTable data={filteredRows} columns={columns} />

        <AlertConfirm
          open={openAlertConfirm}
          title="Confirmar cambio de estado"
          message={`¿Está seguro que desea ${
            selectedMethod?.checked ? 'activar' : 'desactivar'
          } este método de pago?`}
          onClose={() => {
            setOpenAlertConfirm(false);
            setSelectedMethod(null);
          }}
          onConfirm={confirmChangeState}
        />

        <AlertConfirm
          open={openAlertConfirmDelete}
          title="Confirmar eliminación"
          message="¿Está seguro que desea eliminar este método de pago? Esta acción no se puede deshacer."
          onClose={() => {
            setOpenAlertConfirmDelete(false);
            setSelectedMethod(null);
          }}
          onConfirm={confirmDelete}
        />

        <Toaster
          show={alert.show}
          type={alert.type}
          text={alert.text}
          onClose={() => setAlert({ show: false, type: 'success', text: '' })}
        />
      </SnackbarProvider>
    </>
  );
};

export default PaymentMethodsTable;
